import { useMemo } from 'react'
import JsonFormsWrapper from '@/components/common/jsonForms/jsonFormsWrapper/JsonFormsWrapper'
import { SectionTitle } from '@/components/molecules'
import { PageContainer } from '@/components/organisms'
import {
  activitySelectionUiSchema,
  availabilityJsonSchema,
  availabilityUiSchema,
  pointsAllocationJsonSchema,
  pointsAllocationJsonSchemaForEG,
  pointsAllocationUiSchema,
  pointsAllocationUiSchemaForEG,
  welcomeActivityAvailabilityJsonSchema,
  welcomeActivityAvailabilityUiSchema,
  welcomeActivityEndDateJsonSchema,
  welcomeActivityEndDateUiSchema,
  welcomeActivityHasEndDateSchema,
  welcomeActivityHasEndUiSchema,
  allAvailabilityUiSchema
} from '@/services/schema/activity'
import LoginActivitySetUp from '../LoginActivitySetUp'
import PointsOnOrderDeliverySetUp from '../PointsOnOrderDeliverySetUp'
import WelcomeActivitySetUp from '../WelcomeActivitySetUp'
import useActivitySetup from '@/containers/Activity/ActivitySetUp/ActivitySetup-hook'
import { ACTIVITY_TYPE } from '@/types/Activity'
import { TableSkeletonLoader } from '@/components/atoms'
import { COUNTRY_CODES_FREQUENCY } from '@/utils/constants/globalConstants'
import { useAppSelector } from '@/store'
import DuplicateAlert from '@/components/atoms/Toast/DuplicateAlert'
// Helper: Activity Selection
export const ActivitySelection = ({ activityDropdownTypes, formData, handleResetFormData }) => (
  activityDropdownTypes && Object.keys(activityDropdownTypes)?.length > 0 ? (
    <JsonFormsWrapper
      jsonschema={activityDropdownTypes}
      uischema={activitySelectionUiSchema(formData)}
      data={formData}
      onChange={data => handleResetFormData(data)}
    />
  ) : null
)

// Helper: Activity Type Component
export const ActivityTypeComponent = ({ activityType, formData, handleFormChange }) => {
  switch (activityType) {
    case ACTIVITY_TYPE.WELCOME_ACTIVITY:
      return <div data-testid="welcome-activity"><WelcomeActivitySetUp formData={formData} handleFormChange={handleFormChange} /></div>
    case ACTIVITY_TYPE.LOGIN:
      return <div data-testid="login-activity"><LoginActivitySetUp formData={formData} handleFormChange={handleFormChange} /></div>
    case ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY:
      return <div data-testid="points-activity"><PointsOnOrderDeliverySetUp formData={formData} handleFormChange={handleFormChange} /></div>
    default:
      return null
  }
}

// Helper: Points Allocation Preview
const PointsAllocationPreview = ({ isCountryCodeFrequency, numberOfCases, localCurrency, localCurrencyCode, pointsEarned }) => (
  <section className="preview-point-allocation float-right">
    <div className="title">PREVIEW</div>
    <div>
      <div className="preview-value">
        {isCountryCodeFrequency ? "Buy" : "Spend "}
        &nbsp;
        <span className="font-bold">{isCountryCodeFrequency ? numberOfCases : localCurrency}</span> &nbsp;
        {isCountryCodeFrequency ? "cases" : localCurrencyCode} to earn <span className="font-bold">{pointsEarned}</span> Points.
      </div>
    </div>
  </section>
)

// Helper: Welcome Activity Availability
const WelcomeActivityAvailability = ({ formData, handleFormChange }) => (
  <>
    <div className="flex items-center">
      <p className="mb-5 mt-4 flex w-6 text-base font-bold">Availability</p>
    </div>
    <div className="flex flex-wrap gap-14">
      <div className="min-w-[288px] z-50 relative">
        <JsonFormsWrapper
          jsonschema={welcomeActivityAvailabilityJsonSchema}
          uischema={welcomeActivityAvailabilityUiSchema(formData?.endDate || '', formData?.startDate || '', formData)}
          data={formData}
          onChange={data => handleFormChange(data)}
          formData={formData}
        />
      </div>
      <div className="ml-6 min-w-[200px] z-30 relative">
        <JsonFormsWrapper
          jsonschema={welcomeActivityHasEndDateSchema}
          uischema={welcomeActivityHasEndUiSchema}
          data={formData}
          onChange={data => handleFormChange(data)}
          formData={formData}
        />
      </div>
      {formData.hasEndDate && (
        <div className="min-w-[288px] relative">
          <JsonFormsWrapper
            jsonschema={welcomeActivityEndDateJsonSchema}
            uischema={welcomeActivityEndDateUiSchema(formData?.startDate, formData?.endDate)}
            data={formData}
            onChange={data => handleFormChange(data)}
            formData={formData}
          />
        </div>
      )}
    </div>
  </>
)

const ActivitySetUp = ({ 
  localCurrencyCode, 
  showToast, 
  toastMessage, 
  handleToastClose, 
  handleViewActivity 
}) => {
  const { formData, activityDropdownTypes, handleFormChange, handleResetFormData, isLoading } = useActivitySetup()

  const getSchemawithLocalCurrency = (schema, localCurrency, isCountryCodeFrequency = false) => {
    if (isCountryCodeFrequency) {
      schema.elements[0].elements[0].label = `Number of Cases*`
    }
    else {
      schema.elements[0].elements[0].label = `Local Currency (${localCurrency})*`
    }
    return schema
  }
  const countryCode = useAppSelector(state => state.countryCode).countryCode
  const isCountryCodeFrequency = useMemo(() => COUNTRY_CODES_FREQUENCY.includes(countryCode), [countryCode]);
  const { activityType, localCurrency, pointsEarned, numberOfCases } = formData
  const showPreview = (localCurrency > 0 || numberOfCases > 0) && pointsEarned > 0
  const uiSchema = activityType === 'Login'
    ? allAvailabilityUiSchema(formData?.startDate || '', formData?.endDate, formData)
    : availabilityUiSchema(formData?.startDate || '', formData?.endDate, formData);
  return (
    <PageContainer data-testid="activity-setup-content" minHeight={'64vh'}>
      <>
        <div data-testid="activity-setup-content" className="flex items-center justify-between">
          <SectionTitle label={'Activity Setup'} />
        </div>
        {showToast && (
          <DuplicateAlert 
            type='info' 
            message={toastMessage} 
            show={true} 
            onCloseClick={handleToastClose} 
            handleClick={handleViewActivity}
          />
        )}
        {isLoading && <TableSkeletonLoader rows={6} spacing={30} />}
        {/* Activity Selection */}
        <div className="mb-6 flex flex-col lg:flex-row gap-12">
          <ActivitySelection activityDropdownTypes={activityDropdownTypes} formData={formData} handleResetFormData={handleResetFormData} />
          <ActivityTypeComponent activityType={formData?.activityType} formData={formData} handleFormChange={handleFormChange} />
        </div>
        {/* Ponts on Order Delivery Ponts Allocation */}
        {activityType == ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY && (
          <div className="mb-6" data-testid="activity-point-allocation">
            <SectionTitle label={isCountryCodeFrequency ? "Point Allocation (Number of Cases)" : "Point Allocation"} />
            <div className='flex flex-wrap'>
              <JsonFormsWrapper
                jsonschema={isCountryCodeFrequency ? pointsAllocationJsonSchemaForEG : pointsAllocationJsonSchema}
                uischema={isCountryCodeFrequency ? pointsAllocationUiSchemaForEG : getSchemawithLocalCurrency(pointsAllocationUiSchema, localCurrencyCode, isCountryCodeFrequency)}
                data={formData}
                onChange={data => handleFormChange(data)}
              />
              {showPreview && (
                <PointsAllocationPreview
                  isCountryCodeFrequency={isCountryCodeFrequency}
                  numberOfCases={numberOfCases}
                  localCurrency={localCurrency}
                  localCurrencyCode={localCurrencyCode}
                  pointsEarned={pointsEarned}
                />
              )}
            </div>
          </div>
        )}
        {/* Welcome Activity Availability*/}
        {activityType == ACTIVITY_TYPE.WELCOME_ACTIVITY && (
          <WelcomeActivityAvailability formData={formData} handleFormChange={handleFormChange} />
        )}
        {/* Login Activity & Points on Order Delivery Availability */}
        {(activityType == 'Login' || activityType == ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY) && (
          <div data-testid="activity-availability" className="mb-8">
            <p className="mb-5 mt-4 text-base font-bold">Availability</p>
            <JsonFormsWrapper
              jsonschema={availabilityJsonSchema}
              uischema={uiSchema}
              data={formData}
              onChange={data => handleFormChange(data)}
            />
          </div>
        )}
      </>
    </PageContainer>
  )
}

export default ActivitySetUp
